import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { App, Skeleton } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Tooltip from "components/ui/tooltip/Tooltip";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { BidReportEntry_Answer_Sources } from "lib/generated/app-service-gql/graphql";
import {
  CustomerDocumentDetails,
  DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  useCustomerDocuments,
} from "lib/hooks/api/customer_documents/useCustomerDocuments";
import {
  useDownloadCustomerDocument,
  useDownloadCustomerDocuments,
} from "lib/hooks/api/customer_documents/useDownloadCustomerDocument";
import { useTriggerBidDocumentUpload } from "lib/hooks/api/customer_documents/useTriggerBidDocumentUpload";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { red500, sysPrimaryDefault, sysTextDefault } from "lib/themes/colors";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentStatus,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import { EventNames, useTracking } from "../../../lib/tracking";
import { UploadTenderDocumentModal } from "../../bids/bid_documents/UploadTenderDocumentModal";
import { useDocumentUploadProgress } from "../../content_library/DocumentUploadProgressProvider";
import { DeleteBidDocumentModal, RegenerateAnswersModal } from "./ReportModals";

import css from "./DocumentSlideout.module.scss";

type Props = {
  bidId: string;
  onSelectDocument: (source: BidReportEntry_Answer_Sources) => Promise<void>;
  onRegenerateBidReport: () => void;
  hasEditAccess: boolean;
};

export function DocumentSlideout({
  bidId,
  onSelectDocument,
  onRegenerateBidReport,
  hasEditAccess,
}: Props) {
  const { message } = App.useApp();
  const { logEvent } = useTracking();
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const { data: unsortedData, isLoading } = useCustomerDocuments(
    {
      resourceId: bidId,
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
      statuses: [CustomerDocumentStatus.Success, CustomerDocumentStatus.Failed],
    },
    DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION,
  );

  const data = useMemo(
    () =>
      unsortedData?.sort((a, b) => {
        if (a.status === b.status) return 0;
        if (a.status === CustomerDocumentStatus.Success) return -1;
        if (b.status === CustomerDocumentStatus.Success) return 1;
        return 0;
      }),
    [unsortedData],
  );

  const failedDocumentsCount = useMemo(
    () => unsortedData?.filter((doc) => doc.status === CustomerDocumentStatus.Failed).length,
    [unsortedData],
  );

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const triggerUpload = useTriggerBidDocumentUpload(bidId);
  const { filesProcessing } = useDocumentUploadProgress(bidId);
  const prevFilesProcessingRef = useRef(filesProcessing);

  const wasUploadFromSlideoutRef = useRef(false);

  // Open regenerate modal when files finish processing after an upload from the slideout
  useEffect(() => {
    const wasProcessing = prevFilesProcessingRef.current;
    prevFilesProcessingRef.current = filesProcessing;

    if (wasProcessing && !filesProcessing && wasUploadFromSlideoutRef.current) {
      wasUploadFromSlideoutRef.current = false;
      setIsRegenerateModalOpen(true);
    }
  }, [filesProcessing]);

  const { mutate: downloadAllDocuments } = useDownloadCustomerDocuments({
    onError: (error) => {
      console.error(`Error downloading all documents: ${JSON.stringify(error)}`);
      message.error(`Failed to download all documents`);
    },
  });

  return (
    <div className={css.tenderDocuments}>
      <div className={css.header}>
        <Text h2>Tender documents ({data?.length ?? 0})</Text>

        {hasEditAccess && data && data.length > 0 && (
          <Tooltip content="Download all" side="bottom">
            <Button
              buttonType="tertiary"
              icon="download01"
              iconProps={{
                size: 20,
                color: sysTextDefault,
              }}
              onClick={() => {
                downloadAllDocuments({
                  documents: data,
                  zipFileName: `stotles_bid_documents_${bidId}.zip`,
                });
              }}
            />
          </Tooltip>
        )}
      </div>
      <Flex flexDirection="column" style={{ marginTop: "24px" }} gap={16}>
        {isLoading || !data ? (
          <Skeleton />
        ) : (
          <>
            {failedDocumentsCount > 0 && <FailedDocumentsMessage count={failedDocumentsCount} />}
            {data.map((doc: CustomerDocumentDetails) => (
              <DocumentRow
                key={doc.id}
                document={doc}
                onSelectDocument={onSelectDocument}
                hasEditAccess={hasEditAccess}
              />
            ))}
          </>
        )}
        {hasEditAccess && (
          <Button
            icon="plus"
            buttonType="secondary"
            iconProps={{ color: sysPrimaryDefault }}
            onClick={() => setIsUploadModalOpen(true)}
          >
            Upload document
          </Button>
        )}
      </Flex>
      <UploadTenderDocumentModal
        bidId={bidId}
        isOpen={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        sourceUrl={undefined}
        onSubmit={(files) => {
          logEvent(EventNames.additionalDocumentsUploaded, {
            "Action type": "Button clicked",
            "Files added": files.length,
          });
          wasUploadFromSlideoutRef.current = true;
          triggerUpload(files);
        }}
      />
      <RegenerateAnswersModal
        isOpen={isRegenerateModalOpen}
        modalText="New documents have been uploaded. Would you like to regenerate the report now?"
        onRegenerate={onRegenerateBidReport}
        onClose={() => setIsRegenerateModalOpen(false)}
      />
    </div>
  );
}

function DocumentRow({
  document,
  onSelectDocument,
  hasEditAccess,
}: {
  document: CustomerDocumentDetails;
  hasEditAccess: boolean;
  onSelectDocument: (source: BidReportEntry_Answer_Sources) => Promise<void>;
}) {
  const { message } = App.useApp();

  const { mutate: downloadDocument } = useDownloadCustomerDocument({
    onError: (error, vars) => {
      console.error(`Error downloading document: ${JSON.stringify(error)}`);
      message.error(`Failed to download document ${vars.fileName}`);
    },
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  return (
    <div
      key={document.id}
      className={css.item}
      onClick={async () =>
        await onSelectDocument({
          documentId: document.id,
          fileName: document.fileName,
          locations: [],
        })
      }
    >
      <span className={css.content}>
        {document.status === CustomerDocumentStatus.Failed ? (
          <UIcon icon="xCircle" className={css.xIcon} />
        ) : (
          <UIcon icon="fileAttachment" className={css.icon} />
        )}
        <EllipsisTooltipText fullText={document.fileName} containerClassname={css.documentText} />
      </span>
      {hasEditAccess && (
        <span className={css.actionItems}>
          <Button
            buttonType="tertiary"
            icon="download01"
            className={css.actionBtn}
            iconProps={{
              size: 16,
              color: sysTextDefault,
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              e.preventDefault();

              downloadDocument(document);
            }}
          />
          <Button
            buttonType="tertiary"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              e.preventDefault();
              setDeleteModalOpen(true);
            }}
            className={css.deleteButton}
            icon="trash03"
            iconProps={{ color: red500 }}
          />
        </span>
      )}
      <DeleteBidDocumentModal
        documentId={document.id}
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />
    </div>
  );
}

function FailedDocumentsMessage({ count }: { count: number }) {
  const [shouldShow, setShouldShow] = React.useState(true);

  if (!shouldShow) return <></>;

  return (
    <FailedDocumentsMessageContainer>
      <Flex alignItems="center" gap={12} flexGrow={1}>
        <UIcon icon="xCircle" color="sysDestructiveDefault" />
        {pluralise(count, "file")} failed to upload
      </Flex>
      <CloseButton icon="xClose" buttonStyle="inverse" onClick={() => setShouldShow(false)} />
    </FailedDocumentsMessageContainer>
  );
}

const FailedDocumentsMessageContainer = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysDestructiveSubtle,
  padding: "10px 16px",
  borderRadius: 4,
}));

const CloseButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.colors.sysDestructiveSubtle,
  ":hover": {
    backgroundColor: theme.colors.sysDestructiveSubtle,
  },
}));
